import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, X, Plus, Loader2, ChevronRight } from 'lucide-react';
import { useCompanyHierarchyStore, type HierarchyLink } from '../../lib/stores/companyHierarchyStore';
import { ContactCompanyLinkPicker } from './ContactCompanyLinkPicker';
import { useEntityDrawer } from '../../contexts/EntityDrawerContext';

interface Props {
  companyId: string;
}

/**
 * Gerarchia aziende (madre/controllate) sulla scheda azienda. Modello
 * Contatti/Aziende first-class (relationship_type='azienda_madre').
 */
export function CompanyHierarchySection({ companyId }: Props) {
  const navigate = useNavigate();
  const { openEntity } = useEntityDrawer();
  // Stessa cosa dei collegamenti persona/azienda: dritti alla scheda, senza
  // il rimbalzo sulla lista clienti.
  const open = (id: string) => { if (openEntity) openEntity(id); else navigate(`/clients/${id}`); };

  const fetchParent = useCompanyHierarchyStore((s) => s.fetchParent);
  const fetchSubsidiaries = useCompanyHierarchyStore((s) => s.fetchSubsidiaries);
  const setParent = useCompanyHierarchyStore((s) => s.setParent);
  const removeParent = useCompanyHierarchyStore((s) => s.removeParent);

  const [parent, setParentState] = useState<HierarchyLink | null>(null);
  const [subs, setSubs] = useState<HierarchyLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [picking, setPicking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [p, s] = await Promise.all([fetchParent(companyId), fetchSubsidiaries(companyId)]);
    setParentState(p);
    setSubs(s);
    setLoading(false);
  }, [companyId, fetchParent, fetchSubsidiaries]);

  useEffect(() => { load(); }, [load]);

  const excludeIds = useMemo(
    () => [companyId, parent?.company.id, ...subs.map((s) => s.company.id)].filter(Boolean) as string[],
    [companyId, parent, subs],
  );

  const handlePickParent = async (picked: { id: string }) => {
    setPicking(false);
    await setParent(companyId, picked.id);
    await load();
  };
  const handleRemoveParent = async () => {
    await removeParent(companyId);
    await load();
  };

  return (
    // Il titolo lo mette la sezione del pannello che la contiene.
    <div className="space-y-1">
      {loading ? (
        <div className="flex items-center gap-2 px-1.5 py-1.5 text-[12px] text-[rgba(55,53,47,0.45)] dark:text-gray-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Caricamento…
        </div>
      ) : (
        <div className="space-y-1">
          {/* Azienda madre */}
          <div className="group/parent flex min-h-[28px] items-center gap-2 rounded px-1.5 py-1 transition-colors hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.03]">
            <span className="w-32 flex-shrink-0 text-[12px] text-[#787774] dark:text-gray-400">Azienda madre</span>
            {parent ? (
              <span className="flex min-w-0 flex-1 items-center gap-1">
                <button onClick={() => open(parent.company.id)} className="flex min-w-0 items-center gap-1.5 text-[13px] text-[#37352F] hover:text-[#337EA9] dark:text-white dark:hover:text-[#529CCA]">
                  <Building2 className="h-3 w-3 flex-shrink-0 text-[#787774] dark:text-gray-500" strokeWidth={1.5} />
                  <span className="truncate">{parent.company.business_name || '—'}</span>
                </button>
                <button onClick={handleRemoveParent} title="Rimuovi madre" className="flex-shrink-0 text-[rgba(55,53,47,0.3)] opacity-0 transition-opacity hover:text-red-500 group-hover/parent:opacity-100 dark:text-gray-600">
                  <X className="h-3.5 w-3.5" strokeWidth={1.5} />
                </button>
              </span>
            ) : picking ? (
              <div className="min-w-0 flex-1">
                <ContactCompanyLinkPicker
                  targetEntityType="company"
                  excludeIds={excludeIds}
                  onPick={handlePickParent}
                  onClose={() => setPicking(false)}
                />
              </div>
            ) : (
              <button onClick={() => setPicking(true)} className="flex items-center gap-0.5 rounded px-1 py-0.5 text-[11px] text-[#337EA9] hover:bg-[rgba(55,53,47,0.06)] dark:text-[#529CCA] dark:hover:bg-white/[0.06]">
                <Plus className="h-3 w-3" strokeWidth={1.5} /> Imposta madre
              </button>
            )}
          </div>

          {/* Controllate */}
          <div className="flex min-h-[28px] items-start gap-2 rounded px-1.5 py-1">
            <span className="w-32 flex-shrink-0 pt-0.5 text-[12px] text-[#787774] dark:text-gray-400">
              Controllate{subs.length > 0 && ` (${subs.length})`}
            </span>
            {subs.length === 0 ? (
              <span className="pt-0.5 text-[12px] text-[rgba(55,53,47,0.45)] dark:text-gray-500">Nessuna</span>
            ) : (
              <ul className="min-w-0 flex-1 space-y-0.5">
                {subs.map((s) => (
                  <li key={s.id}>
                    <button onClick={() => open(s.company.id)} className="flex w-full min-w-0 items-center gap-1 rounded px-1 py-0.5 text-left text-[13px] text-[#37352F] transition-colors hover:bg-[rgba(55,53,47,0.04)] hover:text-[#337EA9] dark:text-white dark:hover:bg-white/[0.03] dark:hover:text-[#529CCA]">
                      <ChevronRight className="h-3 w-3 flex-shrink-0 text-[#787774] dark:text-gray-500" strokeWidth={1.5} />
                      <span className="truncate">{s.company.business_name || '—'}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
